import { useCallback, useEffect, useState, memo } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import {
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Grid2,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
  Zoom,
} from '@mui/material';
import {
  KeyboardArrowDown,
  KeyboardDoubleArrowDown,
  Refresh,
  Sort,
  Flag,
  FolderOff,
} from '@mui/icons-material';
import moment from 'moment';
import { AppBar, FilterInput, Modals } from 'components';
import { useAlert, useApp, useAuth, useDossier } from 'contexts';
import { AuthController, DossierController } from 'controllers';
import { Cnpj, Cpf } from 'utils/masks';
import { DossierPdfStatus, DossierStatus, TypeUser } from 'types/enums';
import { IDossierViewModel, IVoidResponse } from 'viewModels';
import { ContentTable } from './styles';
import { Columns } from 'props';
import NewDossierModal from 'components/Modal/NewDossierModal';
import { FilePlus2 } from 'lucide-react';
import { TableSort } from 'types/entities';
import { notification, Popconfirm } from 'antd';

interface ModalState {
  newDossier: boolean;
  privacyPolicy: boolean;
  confirmPrivacyPolicy: boolean;
  termsConditions: boolean;
  confirmTermsConditions: boolean;
  summaryDossier: boolean;
  previewDossier: boolean;
  generateAgain: boolean;
}

// Table Header Component
const TableHeader = memo(
  ({
    orderBy,
    handleSort,
  }: {
    orderBy: TableSort[];
    handleSort: (key: Columns) => () => void;
  }) => {
    const theme = useTheme();

    const getSortIcon = (key: Columns) => (
      <IconButton
        data-testid="sortIconButton"
        size="small"
        onClick={handleSort(key)}
        aria-label={`Sort by ${key}`}
      >
        {orderBy.find(o => o.key === key) ? (
          <KeyboardArrowDown
            sx={{
              transition: 'transform 0.3s ease-in',
              transform:
                orderBy.find(o => o.key === key)?.direction === 'asc'
                  ? 'rotate(-180deg)'
                  : '',
            }}
          />
        ) : (
          <Sort />
        )}
      </IconButton>
    );

    const columns = [
      { key: 'number', label: '#' },
      { key: 'document', label: 'CPF/CNPJ', sortable: true },
      { key: 'description', label: 'Descrição' },
      { key: 'date', label: 'Data', sortable: true },
      { key: 'status', label: 'Status', sortable: true },
      { key: 'export', label: 'Exportar' },
      { key: 'monitoring', label: 'Monitoramento' },
      { key: 'flag', label: 'Red Flag' },
      { key: 'summary', label: 'Sumário' },
      { key: 'preview', label: 'Resumo' },
      { key: 'actions', label: '' },
    ];

    return (
      <TableHead>
        <TableRow
          sx={{
            th: {
              backgroundColor: 'white',
              '& p': {
                fontWeight: 'bold',
                color: theme.palette.primary.main,
              },
              '& div': {
                display: 'flex',
                alignItems: 'center',
                '& button': { ml: 1 },
              },
            },
          }}
        >
          {columns.map(col => (
            <TableCell
              key={col.key}
              sx={
                col.key === 'actions' ? { width: '40px', minWidth: '40px' } : {}
              }
            >
              <Grid>
                <Typography>{col.label}</Typography>
                {col.sortable && getSortIcon(col.key as Columns)}
              </Grid>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }
);

// Table Row Component
const TableRowDossier = memo(
  ({
    dossier,
    index,
    handleFailErrorDossier,
    handleModalFailInfo,
    handleRequestNewTry,
    handleRequestPdf,
    setShowGenerateAgain,
    setShowSummaryDossier,
    setShowPreviewDossier,
    setDossierSelected,
    handleCancelDossie,
    user,
  }: {
    dossier: IDossierViewModel;
    index: number;
    handleFailErrorDossier: (id: string) => void;
    handleModalFailInfo: () => void;
    handleRequestNewTry: (id: string) => void;
    handleRequestPdf: (id: string, option: 'pdf' | 'zip') => void;
    setShowGenerateAgain: (value: boolean) => void;
    setShowSummaryDossier: (value: boolean) => void;
    setShowPreviewDossier: (value: boolean) => void;
    setDossierSelected: (dossier: IDossierViewModel) => void;
    handleCancelDossie: (id: string) => void;
    user: any;
  }) => {
    const labelTypeDossier = useCallback((dossierStatus?: DossierStatus) => {
      if (dossierStatus === DossierStatus.PROCESSING_ERROR) return 'Falha';
      if (dossierStatus === DossierStatus.PENDING) return 'Pendente';
      if (dossierStatus === DossierStatus.PROCESSING) return 'Processando';
      if (dossierStatus === DossierStatus.DONE) return 'Finalizado';

      if (dossierStatus === DossierStatus.CANCELED) return 'Cancelado';
      return 'Falha';
    }, []);

    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Pendente':
          return 'warning';
        case 'Processando':
          return 'info';
        case 'Finalizado':
          return 'success';
        case 'Cancelado':
          return 'default';
        default:
          return 'error';
      }
    };

    return (
      <TableRow
        data-testid="tableRowDossier"
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      >
        <TableCell>
          <Typography color="primary">
            {(index + 1).toString().padStart(3, '0')}
          </Typography>
        </TableCell>
        <TableCell data-testid="tableCellDocument">
          <Typography>
            {dossier.document.length === 11
              ? Cpf(dossier.document)
              : Cnpj(dossier.document)}
          </Typography>
        </TableCell>

        {dossier?.description ? (
          <TableCell data-testid="tableCellDescription" sx={{ width: 10 }}>
            <Tooltip
              title={<h1 style={{ fontSize: 15 }}>{dossier.description}</h1>}
              followCursor
              TransitionComponent={Zoom}
            >
              <Typography className="dossierDescription">
                {dossier?.description}
              </Typography>
            </Tooltip>
          </TableCell>
        ) : (
          <TableCell align="center" sx={{ width: 10 }}>
            <span>-</span>
          </TableCell>
        )}

        <TableCell data-testid="tableCellDate">
          <Typography>
            {moment(dossier.createdAt).format('DD/MM/yyyy HH:mm')}
          </Typography>
        </TableCell>
        <TableCell data-testid="tableCellStatus">
          {dossier.status === DossierStatus.PROCESSING_ERROR ? (
            <button
              className="buttonError"
              onClick={() =>
                user?.typeUser === TypeUser.ADM
                  ? handleFailErrorDossier(dossier.id)
                  : handleModalFailInfo()
              }
              aria-label="View error details"
            >
              <Chip
                data-testid="chipStatusFail"
                style={{ cursor: 'pointer' }}
                label={labelTypeDossier(dossier.status)}
                variant="outlined"
                title={
                  user?.typeUser === TypeUser.ADM
                    ? 'Clique para ver o Log'
                    : 'Clique para instruções'
                }
                color="error"
              />
            </button>
          ) : (
            <Chip
              data-testid="chipStatus"
              label={labelTypeDossier(dossier.status)}
              variant="outlined"
              color={getStatusColor(labelTypeDossier(dossier.status))}
            />
          )}
          {dossier.status === DossierStatus.PROCESSING_ERROR && (
            <IconButton
              data-testid="buttonFail"
              onClick={() => handleRequestNewTry(dossier.id)}
              aria-label="Retry processing"
            >
              <Refresh color="error" />
            </IconButton>
          )}
        </TableCell>
        <TableCell data-testid="tableCellExport">
          <ButtonGroup disabled={dossier.status !== DossierStatus.DONE}>
            {dossier.pdfStatus === DossierPdfStatus.CANCELED ? (
              <Button
                title="Clique para tentar novamente"
                size="small"
                color="error"
                variant="outlined"
                onClick={() => handleRequestPdf(dossier.id, 'pdf')}
                aria-label="Retry PDF generation"
              >
                FALHA
                <Refresh sx={{ pl: 1 }} />
              </Button>
            ) : dossier.pdfStatus === DossierPdfStatus.BUILDING ? (
              <Button
                sx={{ pointerEvents: 'none' }}
                size="small"
                color="warning"
                variant="outlined"
                aria-label="PDF generation in progress"
              >
                Gerando...
              </Button>
            ) : (
              <>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  onClick={() => handleRequestPdf(dossier.id, 'pdf')}
                  aria-label="Download PDF"
                >
                  PDF
                </Button>
                <Button
                  size="small"
                  color="info"
                  variant="outlined"
                  onClick={() => handleRequestPdf(dossier.id, 'zip')}
                  aria-label="Download ZIP"
                >
                  ZIP
                </Button>
              </>
            )}
          </ButtonGroup>
        </TableCell>
        <TableCell data-testid="tableCellMonitoring">
          <Button
            variant="outlined"
            color={
              dossier.generateAgain === true
                ? 'info'
                : dossier.generateAgain === false
                ? 'error'
                : 'warning'
            }
            disabled={dossier.status !== DossierStatus.DONE}
            onClick={() => {
              setShowGenerateAgain(true);
              setDossierSelected(dossier);
            }}
            aria-label={
              dossier.generateAgain === true
                ? 'Monitoring active'
                : dossier.generateAgain === false
                ? 'Monitoring paused'
                : 'Start monitoring'
            }
          >
            {dossier.generateAgain === true
              ? 'Monitorando'
              : dossier.generateAgain === false
              ? 'Pausado'
              : 'Monitorar'}
          </Button>
        </TableCell>
        <TableCell data-testid="tableCellFlag">
          {dossier.status === DossierStatus.DONE ? (
            dossier.hasFlag ? (
              <Flag
                style={{ color: '#e64c3c' }}
                aria-label="Red flag present"
              />
            ) : (
              <Typography>Não possui</Typography>
            )
          ) : (
            <Typography>-</Typography>
          )}
        </TableCell>
        <TableCell data-testid="tableCellSummary">
          {true ? (
            <Button
              variant="outlined"
              color="info"
              onClick={() => {
                setShowSummaryDossier(true);
                setDossierSelected(dossier);
              }}
              aria-label="View summary"
            >
              Visualizar
            </Button>
          ) : (
            <Typography>-</Typography>
          )}
        </TableCell>
        <TableCell data-testid="tableCellPreview">
          {dossier.status === DossierStatus.DONE ? (
            <Button
              variant="outlined"
              color="info"
              onClick={() => {
                setShowPreviewDossier(true);
                setDossierSelected(dossier);
              }}
              aria-label="View preview"
            >
              Preview
            </Button>
          ) : (
            <Typography>-</Typography>
          )}
        </TableCell>
        <TableCell align="center" sx={{ width: '40px', minWidth: '40px' }}>
          {(() => {
            return dossier.status === DossierStatus.PENDING ||
              dossier.status === DossierStatus.PROCESSING ? (
              <Popconfirm
                title="Cancelar Dossiê"
                description="Você tem certeza que deseja cancelar o Dossiê?"
                onConfirm={() => handleCancelDossie(dossier.id)}
                okText="Sim"
                cancelText="Não"
                placement="bottomRight"
              >
                <CloseOutlinedIcon
                  color="error"
                  sx={{
                    cursor: 'pointer',
                    fontSize: '18px',
                    '&:hover': {
                      opacity: 0.7,
                    },
                  }}
                />
              </Popconfirm>
            ) : (
              <div style={{ width: '18px', height: '18px' }} />
            );
          })()}
        </TableCell>
      </TableRow>
    );
  }
);

const DossierTableSkeleton = () => {
  return [...Array(10)].map((_, rowIndex) => (
    <TableRow key={rowIndex} sx={{ height: 56 }}>
      {/* Coluna # */}
      <TableCell>
        <Skeleton width="28px" height={24} />
      </TableCell>

      {/* CPF/CNPJ */}
      <TableCell>
        <Skeleton width="135px" height={24} />
      </TableCell>

      {/* Descrição (com elipse) */}
      <TableCell sx={{ width: 10 }}>
        <Skeleton width="10rem" height={24} />
      </TableCell>

      {/* Data */}
      <TableCell>
        <Skeleton width="120px" height={24} />
      </TableCell>

      {/* Status */}
      <TableCell>
        <Skeleton
          variant="rectangular"
          width={80}
          height={32}
          sx={{ borderRadius: 12 }}
        />
      </TableCell>

      {/* Exportar (PDF e ZIP) */}
      <TableCell>
        <Skeleton
          variant="rectangular"
          width={90}
          height={32}
          sx={{ borderRadius: 1 }}
        />
      </TableCell>

      {/* Monitoramento */}
      <TableCell>
        <Skeleton
          variant="rectangular"
          width={110}
          height={32}
          sx={{ borderRadius: 1 }}
        />
      </TableCell>

      {/* Red Flag */}
      <TableCell>
        <Skeleton width="80px" height={24} />
      </TableCell>

      {/* Sumário */}
      <TableCell>
        <Skeleton
          variant="rectangular"
          width={100}
          height={32}
          sx={{ borderRadius: 1 }}
        />
      </TableCell>

      {/* Preview */}
      <TableCell>
        <Skeleton
          variant="rectangular"
          width={90}
          height={32}
          sx={{ borderRadius: 1 }}
        />
      </TableCell>
      {/* Ações */}
      <TableCell sx={{ width: '40px', minWidth: '40px' }}>
        <Skeleton variant="circular" width={18} height={18} />
      </TableCell>
    </TableRow>
  ));
};

const Search = () => {
  const {
    dossiers,
    setDossiers,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    setFilter,
    orderBy,
    setOrder,
    handleFetchNextPage,
    handleDownload,
    isDownloading,
    createDossier,
  } = useDossier();
  const { setLoading } = useApp();
  const { show: alertShow } = useAlert();
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const [modalState, setModalState] = useState<ModalState>({
    newDossier: false,
    privacyPolicy: false,
    confirmPrivacyPolicy: false,
    termsConditions: false,
    confirmTermsConditions: false,
    summaryDossier: false,
    previewDossier: false,
    generateAgain: false,
  });
  const [dossierSelected, setDossierSelected] = useState<IDossierViewModel>(
    {} as IDossierViewModel
  );

  const handleSort = useCallback(
    (key: Columns) => () => {
      setOrder(prev => {
        const newOrder = [...prev];
        const oldOrderIdx = prev.findIndex(o => o.key === key);
        if (oldOrderIdx === -1) {
          newOrder.push({ key, direction: 'asc' });
        } else if (newOrder[oldOrderIdx].direction === 'asc') {
          newOrder[oldOrderIdx].direction = 'desc';
        } else {
          newOrder.splice(oldOrderIdx, 1);
        }
        return newOrder;
      });
    },
    [setOrder]
  );

  const handleRequestNewTry = useCallback(
    (id: string) => {
      DossierController.requestNewTryToProcess(id).then(() => {
        setDossiers(prev => {
          const newDossiers = [...prev];
          const idx = newDossiers.findIndex(d => d.id === id);
          if (idx !== -1) newDossiers[idx].status = DossierStatus.PENDING;
          return newDossiers;
        });
      });
    },
    [setDossiers]
  );

  const handleRequestPdf = useCallback(
    async (id: string, option: 'pdf' | 'zip') => {
      try {
        const res = await DossierController.requestPdf(id);
        if (res.success) {
          notification.success({
            message: 'Sucesso',
            description: `O ${option.toUpperCase()} está sendo gerado e será baixado automaticamente ao finalizar.`,
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 2,
          });
        }
      } catch (err) {
        const error = (err as AxiosError).response?.data as IVoidResponse;
        if (error.errorCode === 'EX00000') {
          handleDownload(id, option);
          return;
        }
        notification.error({
          message: 'Falha ao solicitar',
          description:
            'Algum erro inesperado aconteceu ao gerar o arquivo.\nTente novamente...',
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 3,
        });
      }
    },
    [handleDownload]
  );

  const handleCancelDossie = (dossierId: string) => {
    DossierController.cancelDossie(dossierId)
      .then(() => {
        notification.success({
          message: 'Dossiê cancelado',
          description: 'O Dossiê foi cancelado com sucesso.',
          placement: 'topRight',
          pauseOnHover: true,
          showProgress: true,
          duration: 2,
        });
      })
      .catch(err => {
        DossierController.cancelDossie(dossierId).then(() => {
          notification.error({
            message: 'Falha ao cancelar',
            description: 'Houve um erro ao cancelar o Dossiê.',
            placement: 'topRight',
            pauseOnHover: true,
            showProgress: true,
            duration: 2,
          });
        });
      });
  };

  const handleFailErrorDossier = useCallback(
    (dossierId: string) => {
      navigate('/logsUser', { state: { dossierId } });
    },
    [navigate]
  );

  const handleModalFailInfo = useCallback(() => {
    alertShow({
      type: 'alert',
      title: 'Alerta:',
      description: 'Por favor, entre em contato com a equipe de suporte.',
      timeout: 3000,
    });
  }, [alertShow]);

  const handleConfirmationConditions = useCallback(() => {
    setModalState(prev => ({
      ...prev,
      confirmTermsConditions: true,
    }));
    setUser(prev => ({
      ...prev,
      acceptedPrivacyPolicy: true,
      acceptedTermConditions: true,
    }));
    AuthController.updateConditionsAccepted(user.id, true);
  }, [setUser, user.id]);

  useEffect(() => {
    setLoading(isDownloading);
  }, [isDownloading, setLoading]);

  useEffect(() => {
    if (!user.acceptedPrivacyPolicy || !user.acceptedTermConditions) {
      setModalState(prev => ({ ...prev, privacyPolicy: true }));
      if (modalState.confirmPrivacyPolicy) {
        setModalState(prev => ({
          ...prev,
          privacyPolicy: false,
          termsConditions: true,
        }));
      }
      if (modalState.confirmTermsConditions) {
        setModalState(prev => ({
          ...prev,
          termsConditions: false,
        }));
      }
    }
  }, [
    user.acceptedPrivacyPolicy,
    user.acceptedTermConditions,
    modalState.confirmPrivacyPolicy,
    modalState.confirmTermsConditions,
  ]);

  const toggleModal = useCallback((key: keyof ModalState, value: boolean) => {
    setModalState(prev => ({ ...prev, [key]: value }));
  }, []);

  return (
    <>
      <AppBar />
      <Container
        sx={{
          minWidth: '90vw',
          minHeight: '100vh',
          pt: 10,
          pb: 4,
        }}
      >
        <Paper
          sx={{
            p: 4,
            mb: 3,
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          }}
        >
          <Grid2 container justifyContent="space-between" alignItems="center">
            <Typography
              component="p"
              variant="h4"
              color="primary"
              sx={{
                fontWeight: 600,
                position: 'relative',
                marginBottom: '24px',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 60,
                  height: 3,
                  backgroundColor: theme.palette.primary.main,
                  borderRadius: 4,
                },
              }}
            >
              Dossiês
            </Typography>
            <Button
              onClick={() => toggleModal('newDossier', true)}
              variant="contained"
              aria-label="Create new dossier"
              startIcon={<FilePlus2 style={{ width: 18 }} />}
            >
              Novo Dossiê
            </Button>
          </Grid2>
          <ContentTable>
            <FilterInput onInputChange={setFilter} />
            <Table stickyHeader aria-label="Dossier list table">
              <TableHeader orderBy={orderBy} handleSort={handleSort} />
              <TableBody>
                {isLoading ? (
                  <>{DossierTableSkeleton()}</>
                ) : dossiers.length === 0 && !hasNextPage ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center">
                      <Grid2
                        container
                        direction="column"
                        alignItems="center"
                        justifyContent="center"
                        sx={{ minHeight: '100px', py: 4 }}
                      >
                        <FolderOff
                          sx={{
                            fontSize: 40,
                            color: theme.palette.text.secondary,
                            mb: 2,
                          }}
                          aria-label="No dossiers icon"
                        />
                        <Typography
                          variant="h6"
                          color="text.secondary"
                          sx={{ fontWeight: 500 }}
                        >
                          Nenhum dossiê encontrado
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 1, fontStyle: 'italic' }}
                        >
                          Crie um novo dossiê para começar!
                        </Typography>
                      </Grid2>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {dossiers.map((dossier, idx) => (
                      <TableRowDossier
                        key={dossier.id}
                        dossier={dossier}
                        index={idx}
                        handleFailErrorDossier={handleFailErrorDossier}
                        handleModalFailInfo={handleModalFailInfo}
                        handleRequestNewTry={handleRequestNewTry}
                        handleCancelDossie={handleCancelDossie}
                        handleRequestPdf={handleRequestPdf}
                        setShowGenerateAgain={value =>
                          toggleModal('generateAgain', value)
                        }
                        setShowSummaryDossier={value =>
                          toggleModal('summaryDossier', value)
                        }
                        setShowPreviewDossier={value =>
                          toggleModal('previewDossier', value)
                        }
                        setDossierSelected={setDossierSelected}
                        user={user}
                      />
                    ))}

                    {!isLoading && dossiers.length > 0 && hasNextPage && (
                      <TableRow>
                        <TableCell colSpan={11} align="center">
                          {isFetchingNextPage ? (
                            <Grid2
                              container
                              alignItems="center"
                              justifyContent="center"
                              sx={{ minHeight: '65px' }}
                            >
                              <CircularProgress size={15} />
                              <Typography
                                variant="caption"
                                color="text"
                                sx={{ fontStyle: 'italic', pl: 1 }}
                              >
                                Carregando itens...
                              </Typography>
                            </Grid2>
                          ) : (
                            <Button
                              onClick={() => handleFetchNextPage()}
                              sx={{ px: 2 }}
                              aria-label="Load more dossiers"
                            >
                              <KeyboardDoubleArrowDown />
                              <Typography paddingLeft={2} variant="button">
                                Carregar mais...
                              </Typography>
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    )}
                  </>
                )}
              </TableBody>
            </Table>
          </ContentTable>
        </Paper>

        {modalState.newDossier && (
          <NewDossierModal
            open={modalState.newDossier}
            onClose={() => toggleModal('newDossier', false)}
            onSubmit={dossieData => {
              createDossier(dossieData);
              toggleModal('newDossier', false);
            }}
          />
        )}
        {modalState.privacyPolicy && (
          <Modals.PrivacyPolicyModal
            isOpen={modalState.privacyPolicy}
            onClose={() => toggleModal('privacyPolicy', false)}
            onConfirm={() => toggleModal('confirmPrivacyPolicy', true)}
          />
        )}
        {modalState.termsConditions && (
          <Modals.TermsConditions
            isOpen={modalState.termsConditions}
            onClose={() => toggleModal('termsConditions', false)}
            onConfirm={handleConfirmationConditions}
          />
        )}
        {modalState.summaryDossier && (
          <Modals.SummaryDossier
            onClose={() => toggleModal('summaryDossier', false)}
            open={modalState.summaryDossier}
            dossier={dossierSelected}
          />
        )}
        {modalState.previewDossier && (
          <Modals.DossierPreview
            onClose={() => toggleModal('previewDossier', false)}
            open={modalState.previewDossier}
            dossier={dossierSelected}
          />
        )}
        {modalState.generateAgain && (
          <Modals.GenerateAgain
            dossier={dossierSelected}
            open={modalState.generateAgain}
            onClose={() => toggleModal('generateAgain', false)}
          />
        )}
      </Container>
    </>
  );
};

export default Search;
